import { useState, useEffect } from 'react';
import { isFirstTimeUser, isUserSubscribed, getNotificationPermission } from '../lib/oneSignal';

/**
 * Hook to manage first-time notification prompt
 * Shows modal on first PWA login if user hasn't been prompted before
 */
export const useNotificationPrompt = (profile) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

  useEffect(() => {
    checkShouldShowPrompt();
  }, [profile]);

  const checkShouldShowPrompt = async () => {
    // CRITICAL: Don't show to unauthenticated users
    if (!profile?.id) {
      console.log('[NotificationPrompt] No profile - user not logged in');
      return;
    }

    // Only check once per session after login
    if (hasCheckedOnce) {
      return;
    }

    // Check if OneSignal is ready
    if (!window.OneSignal) {
      // Wait for OneSignal to load
      setTimeout(checkShouldShowPrompt, 1000);
      return;
    }

    setIsReady(true);
    setHasCheckedOnce(true); // Mark that we've checked

    try {
      // Don't show if already subscribed
      const subscribed = await isUserSubscribed();
      if (subscribed) {
        console.log('[NotificationPrompt] User already subscribed');
        return;
      }

      // Don't show if permission already denied
      const permission = getNotificationPermission();
      if (permission === 'denied') {
        console.log('[NotificationPrompt] Permission denied');
        return;
      }

      // Don't show if already prompted before
      const firstTime = isFirstTimeUser();
      if (!firstTime) {
        console.log('[NotificationPrompt] User already prompted before');
        return;
      }

      // Check session storage to see if user just logged in
      let justLoggedIn = false;
      try { justLoggedIn = sessionStorage.getItem('just_logged_in'); } catch {}
      if (!justLoggedIn) {
        console.log('[NotificationPrompt] Not a fresh login - skipping prompt');
        return;
      }

      // Clear the flag so it doesn't show again this session
      try { sessionStorage.removeItem('just_logged_in'); } catch {}

      // Show prompt if:
      // 1. First time user
      // 2. Not subscribed
      // 3. Permission not denied
      // 4. User just logged in
      if (firstTime && !subscribed && permission !== 'denied') {
        console.log('[NotificationPrompt] Showing notification prompt after login');
        // Delay to let the dashboard load first
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
      }
    } catch (error) {
      console.error('Error checking notification prompt:', error);
    }
  };

  const handleClose = (enabled) => {
    setShowPrompt(false);
  };

  return {
    showPrompt,
    setShowPrompt,
    handleClose,
    isReady
  };
};
